import { put, takeEvery, takeLeading } from 'redux-saga/effects';

import { resourceSagaUtils, resourceStoreTypes } from 'app-components/flux/core';
import type { QcsSyncResourcesConfigProps } from 'app-components/flux/core/resourceStoreTypes';

import * as actions from './a2aTransfersActions';
import type { A2aTransferProps } from './a2aTransfersTypes';
import { mkA2aTransfer } from './a2aTransfersTypes';
import { isTransferUnavailableError } from './a2aTransfersUtils';

const mkQcsA2aTransfersConfig = resourceStoreTypes.createTypedQcsSyncResourcesConfigFactory<A2aTransferProps>();

// Idk why they have data in data, but I didnt make the API
type getResponse = { data?: { data: A2aTransferProps[]; metaData: any } } | null | undefined;

const transformGetResponseToResources = (response?: getResponse) =>
  response?.data?.data?.map ? response.data?.data?.map(mkA2aTransfer) : [];

const mkResourceConfig = (props: Partial<QcsSyncResourcesConfigProps<A2aTransferProps>>) =>
  mkQcsA2aTransfersConfig({
    resourceName: 'a2aTransfer',
    resourceBaseUrl: '/aikya-transfer',
    transformResourceWithResponse: (resource, response?: { data?: A2aTransferProps }) =>
      response?.data ? mkA2aTransfer(response.data) : undefined,
    transformResponseToResources: (response) => transformGetResponseToResources(response as getResponse),
    ...props,
  });

function* getA2aTransfersActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.getA2aTransfersSuccess,
    failureAction: actions.getA2aTransfersFailure,
    resourceBaseUrl: '/aikya-transfer/api/v1/transfer',
  });

  yield takeLeading(
    actions.getA2aTransfers.type,
    resourceSagaUtils.qcsSyncGetResources<A2aTransferProps>,
    resourceConfig,
  );
}

function* getA2aTransferInfoActionWatcher() {
  yield takeEvery(actions.getA2aTransferInfo, resourceSagaUtils.qcsRequest, {
    method: 'POST',
    path: '/aikya-transfer/api/v1/transfer-info',
    data: (action: any) => action.payload,
    // The transfer-unavailable case already has its own dedicated UI, so skip the generic error snackbar for it.
    showError: (error: any) => !isTransferUnavailableError(error),
  });
}

function* cancelA2aTransferActionWatcher() {
  const resourceConfig = mkResourceConfig({
    successAction: actions.cancelA2aTransferSuccess,
    failureAction: actions.cancelA2aTransferFailure,
    resourceBaseUrl: (action) => `/aikya-transfer/api/v1/transfer/${action.payload}/cancel`,
  });

  yield takeEvery(
    actions.cancelA2aTransfer.type,
    resourceSagaUtils.qcsSyncUpdateResources<A2aTransferProps>,
    resourceConfig,
  );
}

// GET after a success so the user will see immediate feedback
function* cancelA2aTransferSuccessActionWatcher() {
  yield takeEvery(actions.cancelA2aTransferSuccess.type, function* () {
    yield put(actions.getA2aTransfers());
  });
}

// GET after a failure because if a transfer moved from scheduled to processing the user will see immediate feedback
function* cancelA2aTransferFailureActionWatcher() {
  yield takeEvery(actions.cancelA2aTransferFailure.type, function* () {
    yield put(actions.getA2aTransfers());
  });
}

export default [
  getA2aTransfersActionWatcher,
  getA2aTransferInfoActionWatcher,
  cancelA2aTransferActionWatcher,
  cancelA2aTransferSuccessActionWatcher,
  cancelA2aTransferFailureActionWatcher,
];
